import { getMomentTz } from "@dexteel/mesf-core";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import { toFraction32 } from "@/utils/fraction";
import { OverviewLastBar } from "../models/types";
import { microLabelSx } from "../styles";

const moment = getMomentTz();

interface Props {
	bars: OverviewLastBar[];
	loading?: boolean;
}

const LastBarsSkeleton = () => (
	<>
		<Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
		{Array.from({ length: 5 }).map((_, i) => (
			<Box
				key={i}
				sx={{
					display: "grid",
					gridTemplateColumns: "60px 1fr 1fr",
					columnGap: 0.5,
					py: 0.35,
					px: 0.5,
				}}
			>
				<Skeleton variant="rounded" width={40} height={14} />
				<Skeleton variant="text" />
				<Skeleton variant="text" />
			</Box>
		))}
	</>
);

const fmtLen = (n: number | null | undefined) =>
	n === null || n === undefined ? "-" : `${n.toFixed(2)}"`;

const FractionCaption = ({ value }: { value: number }) => (
	<Typography
		sx={{
			fontSize: "0.65rem",
			color: "#999",
			lineHeight: 1.1,
			fontVariantNumeric: "tabular-nums",
		}}
	>
		{toFraction32(value)}
	</Typography>
);

const fmtCycle = (ms: number | null | undefined) =>
	ms === null || ms === undefined ? "-" : `${(ms / 1000).toFixed(1)}s`;

const fmtTime = (d: Date) => moment(d).format("HH:mm:ss");

const lengthColor = (status: OverviewLastBar["LengthStatus"]) => {
	if (status === "LONG" || status === "SHORT") return "#d32f2f";
	if (status === "OK") return "#222";
	return "#999";
};

export const LastBarsCard = ({ bars, loading }: Props) => {
	if (loading) return <LastBarsSkeleton />;
	const specs = bars.find(
		(b) =>
			b.LengthTarget != null ||
			b.LengthLowerLimit != null ||
			b.LengthUpperLimit != null,
	);
	return (
		<>
			<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
				Last 5 bars - cut length
			</Typography>

			{bars.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
					No bars yet.
				</Typography>
			) : (
				<Box>
					{/* Header */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "60px 1fr 1fr",
							alignItems: "baseline",
							columnGap: 0.5,
							py: 0.3,
							px: 0.5,
							borderBottom: "1px solid #e0e0e0",
						}}
					>
						<Box />
						<Typography sx={{ ...microLabelSx, textAlign: "center" }}>
							Length
						</Typography>
						<Typography sx={{ ...microLabelSx, textAlign: "right" }}>
							Cycle
						</Typography>
					</Box>

					{bars.map((b, idx) => {
						const isLatest = idx === 0;
						return (
							<Box
								key={b.BarId}
								sx={{
									display: "grid",
									gridTemplateColumns: "60px 1fr 1fr",
									alignItems: "center",
									columnGap: 0.5,
									py: 0.25,
									px: 0.5,
									"&:hover": { backgroundColor: "#fafafa" },
								}}
							>
								<Box>
									{isLatest ? (
										<Chip
											label="Latest"
											size="small"
											sx={{
												height: 14,
												fontSize: "0.55rem",
												fontWeight: 700,
												backgroundColor: "#e8f5e9",
												color: "#2e7d32",
												border: "1px solid #4CAF50",
												"& .MuiChip-label": { px: 0.5 },
											}}
										/>
									) : null}
								</Box>
								<Box
									title={`Cut time: ${fmtTime(b.Timestamp)}`}
									sx={{ textAlign: "center" }}
								>
									<Typography
										sx={{
											fontSize: "0.85rem",
											fontWeight: isLatest ? 700 : 600,
											color: lengthColor(b.LengthStatus),
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{fmtLen(b.BarLength)}
									</Typography>
									{b.BarLength != null && (
										<FractionCaption value={b.BarLength} />
									)}
								</Box>
								<Typography
									sx={{
										color: "#888",
										fontSize: "0.75rem",
										textAlign: "right",
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{fmtCycle(b.CycleMilliseconds)}
								</Typography>
							</Box>
						);
					})}

					<Box
						sx={{
							mt: 0.5,
							pt: 0.5,
							px: 0.5,
							borderTop: "1px solid #e0e0e0",
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							columnGap: 0.5,
						}}
					>
						{(
							[
								{ label: "Target", value: specs?.LengthTarget },
								{ label: "Max", value: specs?.LengthUpperLimit },
								{ label: "Min", value: specs?.LengthLowerLimit },
							] as const
						).map((col) => (
							<Box key={col.label} sx={{ textAlign: "center" }}>
								<Typography sx={{ ...microLabelSx, display: "block" }}>
									{col.label}
								</Typography>
								<Typography
									sx={{
										fontSize: "0.85rem",
										fontWeight: 700,
										color: col.value == null ? "#bbb" : "#222",
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{fmtLen(col.value)}
								</Typography>
								{col.value != null && <FractionCaption value={col.value} />}
							</Box>
						))}
					</Box>
				</Box>
			)}
		</>
	);
};
